import { useEffect, useState } from 'react'
import { Alert, App, DatePicker, Form, Input, Modal, Radio } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { finaliseBilling, recalculateBillingDraft } from '../services/billing-api'

type TriggerAction = 'draft' | 'finalise'

interface ManualTriggerModalProps {
  open: boolean
  defaultMonth: Dayjs | null
  onClose: () => void
  onComplete: () => void
}

export function ManualTriggerModal({ open, defaultMonth, onClose, onComplete }: ManualTriggerModalProps) {
  const { message } = App.useApp()
  const [month, setMonth] = useState<Dayjs | null>(defaultMonth ?? dayjs())
  const [tenantCode, setTenantCode] = useState('')
  const [action, setAction] = useState<TriggerAction>('draft')
  const [submitting, setSubmitting] = useState(false)

  // Reset the form to the current view's month each time the modal opens.
  useEffect(() => {
    if (open) {
      setMonth(defaultMonth ?? dayjs())
      setTenantCode('')
      setAction('draft')
    }
  }, [open, defaultMonth])

  const period = month ? month.format('YYYYMM') : ''

  const handleRun = async () => {
    if (!period) {
      message.error('Select a month')
      return
    }
    const tenant = tenantCode.trim() || undefined
    const scope = tenant ? `tenant ${tenant}` : 'all tenants'

    setSubmitting(true)
    try {
      if (action === 'finalise') {
        await finaliseBilling(period, tenant)
        message.success(`Finalised ${period} for ${scope}`)
      } else {
        await recalculateBillingDraft(period, tenant)
        message.success(`Recalculated Draft for ${period} (${scope})`)
      }
      onComplete()
      onClose()
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Calculation failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Run billing calculation"
      open={open}
      onCancel={onClose}
      onOk={handleRun}
      okText={action === 'finalise' ? 'Finalise' : 'Recalculate'}
      okButtonProps={{ danger: action === 'finalise' }}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label="Month" required>
          <DatePicker
            picker="month"
            value={month}
            onChange={setMonth}
            format="YYYY-MM"
            allowClear={false}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Tenant code" help="Leave blank to run for all tenants">
          <Input
            placeholder="All tenants"
            value={tenantCode}
            onChange={e => setTenantCode(e.target.value)}
            allowClear
          />
        </Form.Item>
        <Form.Item label="Action">
          <Radio.Group value={action} onChange={e => setAction(e.target.value)}>
            <Radio value="draft">Recalculate Draft</Radio>
            <Radio value="finalise">Finalise</Radio>
          </Radio.Group>
        </Form.Item>
        {action === 'finalise' && (
          <Alert
            type="warning"
            showIcon
            message="Finalising locks the period"
            description="Finalised rows become immutable and cannot be recalculated afterward."
          />
        )}
      </Form>
    </Modal>
  )
}
